import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import expenseService from '../../services/expenseService';
import projectService from '../../services/projectService';
import budgetService from '../../services/budgetService';
import { useAuth } from '../../contexts/AuthContext';

const ExpenseForm = ({ expense, onSubmit, onCancel }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [filteredBudgets, setFilteredBudgets] = useState([]);
  const [formData, setFormData] = useState({
    project_id: expense?.project_id || '',
    budget_id: expense?.budget_id || '',
    expense_type: expense?.expense_type || 'Other',
    description: expense?.description || '',
    amount: expense?.amount || '',
    currency: expense?.currency || 'USD',
    expense_date: expense?.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0],
    receipt_url: expense?.receipt_url || '',
    status: expense?.status || 'Pending',
    notes: expense?.notes || ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadProjects();
    loadBudgets();
  }, []);

  useEffect(() => {
    // Filter budgets based on selected project
    if (formData.project_id) {
      const filtered = budgets.filter(b => b.project_id === formData.project_id);
      setFilteredBudgets(filtered);
    } else {
      setFilteredBudgets(budgets);
    }
  }, [formData.project_id, budgets]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAllProjects();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const loadBudgets = async () => {
    try {
      const data = await budgetService.getAllBudgets();
      setBudgets(data.data || []);
    } catch (error) {
      console.error('Error loading budgets:', error);
      toast.error('Failed to load budgets');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.project_id) {
      newErrors.project_id = 'Project is required';
    }
    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Description is required';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    if (!formData.expense_date) {
      newErrors.expense_date = 'Expense date is required';
    }
    if (!formData.expense_type) {
      newErrors.expense_type = 'Expense type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        user_id: user.id,
        budget_id: formData.budget_id || null
      };

      if (onSubmit) {
        await onSubmit(submitData);
      } else {
        if (expense) {
          await expenseService.updateExpense(expense.id, submitData);
          toast.success('Expense updated successfully');
        } else {
          await expenseService.createExpense(submitData);
          toast.success('Expense created successfully');
        }
        navigate('/app/expenses');
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.error || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/app/expenses');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow space-y-6">
        {/* Project and Budget Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project <span className="text-red-500">*</span>
            </label>
            <select
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.project_id ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select a project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.code})
                </option>
              ))}
            </select>
            {errors.project_id && (
              <p className="mt-1 text-sm text-red-500">{errors.project_id}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget (Optional)
            </label>
            <select
              name="budget_id"
              value={formData.budget_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!formData.project_id}
            >
              <option value="">Select a budget (optional)</option>
              {filteredBudgets.map(budget => (
                <option key={budget.id} value={budget.id}>
                  {budget.category} - {budgetService.formatCurrency(budget.budget_allocated)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {!formData.project_id ? 'Select a project first' : 'Link to a specific budget'}
            </p>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter expense description"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-500">{errors.description}</p>
          )}
        </div>

        {/* Expense Type, Amount, Currency */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expense Type <span className="text-red-500">*</span>
            </label>
            <select
              name="expense_type"
              value={formData.expense_type}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.expense_type ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {expenseService.getExpenseTypes().map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
            {errors.expense_type && (
              <p className="mt-1 text-sm text-red-500">{errors.expense_type}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.amount ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.amount && (
              <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Currency
            </label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {expenseService.getCurrencies().map(curr => (
                <option key={curr.value} value={curr.value}>
                  {curr.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Expense Date and Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expense Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="expense_date"
              value={formData.expense_date}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.expense_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.expense_date && (
              <p className="mt-1 text-sm text-red-500">{errors.expense_date}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!expense}
            >
              {expenseService.getStatuses().map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            {!expense && (
              <p className="mt-1 text-sm text-gray-500">
                New expenses start as Pending
              </p>
            )}
          </div>
      

        {/* Receipt URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Receipt URL
          </label>
          <input
            type="url"
            name="receipt_url"
            value={formData.receipt_url}
            onChange={handleChange}
            placeholder="https://example.com/receipt.pdf"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-sm text-gray-500">
            Link to receipt or supporting document
          </p>
        </div>
  </div>
        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            placeholder="Add any additional notes about this expense..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Expense Summary */}
        {formData.amount && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Expense Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Amount</p>
                <p className="font-semibold text-gray-900">
                  {expenseService.formatCurrency(formData.amount, formData.currency)}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Type</p>
                <p className="font-semibold text-gray-900">
                  {expenseService.getTypeIcon(formData.expense_type)} {formData.expense_type}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Date</p>
                <p className="font-semibold text-gray-900">
                  {expenseService.formatDate(formData.expense_date)}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Status</p>
                <p className={`font-semibold inline-flex items-center px-2.5 py-0.5 rounded-full text-xs ${expenseService.getStatusBadgeClass(formData.status)}`}>
                  {formData.status}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Saving...' : expense ? 'Update Expense' : 'Create Expense'}
        </button>
      </div>
    </form>
  );
};

export default ExpenseForm;

